// web/src/lib/fullscreen.ts
// Native-fullscreen helpers, progressive: standard API → webkit prefix → false.
// iPhone Safari has NO element-fullscreen API at all, so callers must treat native
// fullscreen as an ENHANCEMENT layered onto a CSS "theater" shell (fixed viewport
// overlay) that works everywhere. All helpers are gesture-safe and never throw.

type FsEl = HTMLElement & { webkitRequestFullscreen?: () => unknown };
type FsDoc = Document & {
  webkitExitFullscreen?: () => unknown;
  webkitFullscreenElement?: Element | null;
};

/** Request native fullscreen on `el`. Resolves true iff a native API call succeeded. */
export async function requestAppFullscreen(el: HTMLElement): Promise<boolean> {
  const e = el as FsEl;
  try {
    if (typeof e.requestFullscreen === "function") {
      await e.requestFullscreen();
      return true;
    }
    if (typeof e.webkitRequestFullscreen === "function") {
      await e.webkitRequestFullscreen();
      return true;
    }
  } catch {
    return false; // denied (no gesture) or unsupported mid-flight — theater shell carries it
  }
  return false;
}

/** The element currently in native fullscreen, if any (std or webkit). */
export function nativeFullscreenElement(): Element | null {
  const d = document as FsDoc;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/** Exit native fullscreen if active. Never throws. */
export async function exitAppFullscreen(): Promise<void> {
  const d = document as FsDoc;
  try {
    if (!nativeFullscreenElement()) return;
    if (typeof d.exitFullscreen === "function") await d.exitFullscreen();
    else if (typeof d.webkitExitFullscreen === "function") await d.webkitExitFullscreen();
  } catch {
    /* ignore — the CSS shell exit is what the user sees */
  }
}
